import {
  createSessionStorageConfig,
  createStableJsonCache,
} from '@autone/utils';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { focusAtom } from 'jotai-optics';
import { useMemo } from 'react';

import {
  type AssortmentSessionStorageAtomData,
  type AssortmentSessionStoragePaths,
} from './session-storage-atom.types';

const initialAssortmentSessionStorageData: AssortmentSessionStorageAtomData = {
  currentViewTab: 'productsTable',
  productsTable: {
    generalFilters: {},
    advancedFilters: null,
    extrasMenu: {
      customColumns: [
        'product_name',
        'product_groups',
        'locations_assorted',
        'total_sales_units',
        'first_sale_date',
        'latest_sale_date',
        'store_units',
      ],
    },
  },
  productLocationsTable: {
    generalFilters: {},
    advancedFilters: null,
    extrasMenu: {
      customColumns: [
        'location_desc',
        'current_assortment',
        'initial_allocation',
        'current_units',
        'total_sales_units',
        'first_sale_date',
        'latest_sale_date',
        'location_cluster',
      ],
    },
  },
  productLocationsSkuTable: {},
  skuLocationsTable: {
    generalFilters: {},
    advancedFilters: null,
    extrasMenu: {
      customColumns: [
        'sku_product',
        'size_sku',
        'location',
        'location_cluster',
        'current_assortment',
        'autone_recommendation',
        'initial_allocation',
        'total_sales_units',
        'first_sale_date',
        'latest_sale_date',
        'current_units',
        'warehouse_units',
      ],
      matchSystemRecommendationToggle: 'off',
      managedByAutoneToggle: 'off',
    },
  },
};
const assortmentSessionStorageAtomConfig = createSessionStorageConfig(
  initialAssortmentSessionStorageData,
);

const assortmentSessionStorageAtom = atomWithStorage(
  'assortment-session-storage-v2',
  initialAssortmentSessionStorageData,
  assortmentSessionStorageAtomConfig,
  {
    getOnInit: true,
  },
);

const assortmentSessionStorageSelector = <
  K extends AssortmentSessionStoragePaths,
>(
  path: K,
) => {
  return focusAtom(assortmentSessionStorageAtom, (optic) => optic.path(path));
};

const stabiliseJsonReference = createStableJsonCache();
export const useAssortmentSessionStorageAtom = <
  K extends AssortmentSessionStoragePaths,
>(
  path: K,
) => {
  const focusedAtom = useMemo(
    () => assortmentSessionStorageSelector(path),
    [path],
  );

  const [newAtomValue, setAtomValue] = useAtom(focusedAtom);
  const atomValue = stabiliseJsonReference(path, newAtomValue);

  return [atomValue, setAtomValue] as const;
};
